/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { CardModule } from "primeng/card";
import { DividerModule } from "primeng/divider";
import { ScheduledOrderTotals } from "../../../../../core/models/scheduled-order-totals.interface";

/**
 * Totals display component for showing scheduled order cost breakdown
 * Used in scheduled order create/edit forms to display calculated totals
 */
@Component({
  selector: "app-totals-display",
  standalone: true,
  imports: [CommonModule, CardModule, DividerModule],
  templateUrl: "./totals-display.component.html",
  styleUrl: "./totals-display.component.scss",
})
export class TotalsDisplayComponent {
  @Input() totals?: ScheduledOrderTotals;
  @Input() isLoading = false;
  @Input() showTitle = true;
  @Input() hasDisabledItems = false;

  // Expose Math to template
  Math = Math;

  /**
   * Check if there are any coupon discounts applied
   * @returns True if any coupons are applied
   */
  hasCouponDiscounts(): boolean {
    if (!this.totals) return false;

    const itemsCoupons = this.totals.ItemsTotals?.CouponDiscounts?.length || 0;
    const shippingCoupons =
      this.totals.ShippingTotals?.CouponDiscounts?.length || 0;

    return itemsCoupons + shippingCoupons > 0;
  }

  /**
   * Get all applied coupons from items and shipping
   * @returns Array of all coupon discounts
   */
  getAllCoupons(): any[] {
    if (!this.totals) return [];

    const itemsCoupons = this.totals.ItemsTotals?.CouponDiscounts || [];
    const shippingCoupons = this.totals.ShippingTotals?.CouponDiscounts || [];

    return [...itemsCoupons, ...shippingCoupons];
  }

  /**
   * Get total discount amount from all coupons
   * @returns Total discount amount
   */
  getTotalCouponDiscount(): number {
    if (!this.totals) return 0;

    const itemsDiscount =
      this.totals.ItemsTotals?.CouponDiscounts?.reduce(
        (sum, coupon) => sum + Math.abs(coupon.Amount),
        0,
      ) || 0;

    const shippingDiscount =
      this.totals.ShippingTotals?.CouponDiscounts?.reduce(
        (sum, coupon) => sum + Math.abs(coupon.Amount),
        0,
      ) || 0;

    return itemsDiscount + shippingDiscount;
  }

  /**
   * Check if component has valid totals to display
   * @returns True if totals exist
   */
  hasTotals(): boolean {
    return this.totals !== undefined && this.totals !== null;
  }

  /**
   * Get items subtotal (before discounts)
   * @returns Items subtotal amount
   */
  getItemsSubtotal(): number {
    return this.totals?.ItemsTotals?.SubTotal || 0;
  }

  /**
   * Get items discount total
   * @returns Items discount amount
   */
  getItemsDiscount(): number {
    return this.totals?.ItemsTotals?.DiscountTotal || 0;
  }

  /**
   * Get enabled items subtotal (after price discounts, before coupons)
   * @returns Enabled items subtotal amount
   */
  getEnabledItemsSubtotal(): number {
    return this.getItemsSubtotal() - this.getItemsDiscount();
  }

  /**
   * Get shipping subtotal
   * @returns Shipping subtotal amount
   */
  getShippingSubtotal(): number {
    return this.totals?.ShippingTotals?.SubTotal?.Amount || 0;
  }

  /**
   * Get shipping description
   * @returns Shipping method description
   */
  getShippingDescription(): string {
    return this.totals?.ShippingTotals?.SubTotal?.Description || "Shipping";
  }

  /**
   * Get total tax amount
   * @returns Total tax amount
   */
  getTotalTax(): number {
    return this.totals?.TaxTotals?.Total || 0;
  }

  /**
   * Get order subtotal (items + shipping - discounts, before tax)
   * @returns Order subtotal
   */
  getSubtotal(): number {
    return this.totals?.SubTotal || 0;
  }

  /**
   * Get order grand total
   * @returns Grand total amount
   */
  getGrandTotal(): number {
    return this.totals?.Total || 0;
  }

  /**
   * Get shipping total (after coupons)
   * @returns Shipping total amount
   */
  getShippingTotal(): number {
    return this.totals?.ShippingTotals?.Total || 0;
  }

  /**
   * Get item coupon discounts only
   * @returns Array of item coupon discounts
   */
  getItemCoupons(): any[] {
    return this.totals?.ItemsTotals?.CouponDiscounts || [];
  }

  /**
   * Get shipping coupon discounts only
   * @returns Array of shipping coupon discounts
   */
  getShippingCoupons(): any[] {
    return this.totals?.ShippingTotals?.CouponDiscounts || [];
  }

  /**
   * Get individual tax line items
   * @returns Array of individual taxes
   */
  getIndividualTaxes(): any[] {
    return this.totals?.TaxTotals?.Taxes || [];
  }
}
